import {BehaviorSubject, Observable} from 'rxjs';
import {ApDynGridColumnConfigBase}   from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {ApDynGridPagerConfigBase}    from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {BestScanData}                from '../../../types/scan.types';
import {OperationModesEnum}          from '../../../stores/common/operation-modes.store';
import {IGridStatus}                 from '../../../ap-interface/interfaces/ap-grid-status.interface';
import {IOperationModeAppSetting}    from '../../../types/application-settings.types';
import IGuid = System.IGuid;
import IFieldGeom = Data.FieldManagement.IFieldGeom;
import IPlantProtectionProductDetails = Data.BaseData.IPlantProtectionProductDetails;
import IPlantProtectionProduct = Data.BaseData.IPlantProtectionProduct;
import IPlantProtectionProductList = Data.TaskManagement.IPlantProtectionProductList;
import IPlantProtectionPlanBook = Data.TaskManagement.IPlantProtectionPlanBook;

export enum PpWizardStates {
  Cancel = 'Global__Cancel', // user clicks 'cancel' button on Selection page
  Selection = 'Global__Selection', // first wizard page where user can select fields
  Details = 'Global__Details', // second wizard page configuring the details
  Overview = 'Admin_Pages__Overview', // last wizard page showing summary
  Save = 'Global__Save', // user click 'save' button on Overview page
  Initial = '' // initial state when component is loaded
}

export enum SlopeLevel {
  Low = 'Global__Enum_Low',
  Medium = 'Global__Enum_Medium',
  High = 'Global__Enum_High'
}

export interface IPpConstants {
  MAX_SELECTABLE_PRODUCTS: number;
  MAX_WATER: number;
  PpControlFactors: IPpControlFactor[];
}

export const PpConstants: IPpConstants = {
  MAX_SELECTABLE_PRODUCTS: 6,
  MAX_WATER: 500,
  PpControlFactors: [
    {Id: 1, Key: SlopeLevel.Low},
    {Id: 2, Key: SlopeLevel.Medium},
    {Id: 3, Key: SlopeLevel.High}
  ]
};

export interface IPpControlFactor {
  Id: number;
  Key: string;
}

export interface IPpWizardProvider<T, R> {
  getGridColumnConfig(detailsData: IPpPlanningWizardDetailsData): Observable<ApDynGridColumnConfigBase[]>;
  getPagerConfig(breadCrumbsColumns: PpWizardStates[]): ApDynGridPagerConfigBase;
  onGridSelectionChanged(items: Array<T>): void;
  onGenerateStatistic(): void;
  updateCaption(): void;
  updateFieldSets(): void;
  onLeave(): R;
  onDestroy(): void;
}

export interface IPpPlanningWizardSelectionGridData {
  Id: IGuid;
  Status: IGridStatus;
  Nr: number;
  TNr: number;
  NrTNr: string;
  Name: string;
  Area: number;
  Crop: string;
  CropId: number;
  EppoCode: string;
  SecondCropId: number;
  Geom: IFieldGeom;
  PlanBookId: IGuid;
  ScanData: BestScanData;
}

export interface IPpPlanningWizardOverviewGridData {
  Id: IGuid;
  Status: IGridStatus;
  SlopeForJob: number;
  NUptakeAverageForJob: number;
  Products: string;
  ProductsTooltip: string;
  Machine: string;
  Instrument: string;
  Driver: string;
  ColumnLoading: boolean;
  ErrorMessage: string;
  HasError: boolean;
  SelectionData: IPpPlanningWizardSelectionGridData;
  DetailsData: IPpPlanningWizardDetailsData;
  SelectedProducts: IPpProductListGridItem[];
  PlanBook: IPlantProtectionPlanBook;
}

export interface IPpPlanningWizardDetailsData {
  FormData: IPpPlanningWizardDetailsFormData | undefined;
  ControlFactor: number | undefined;
  OperationModeAppSetting: IOperationModeAppSetting | undefined;
}

export interface IPpPlanningWizardDetailsFormData {
  CountFields: number;
  TotalArea: string;
  Crop: string;
  OperationModeKey: OperationModesEnum | undefined;
  NUptakeAverage: number | undefined;
  WorkType: number;
  ApplicationDate: Date;
  Note: string;
  ECStage: number;
  Threshold: number;
  WaterAvg: number;
  WaterRange: number;
  WaterMin: number;
  WaterMax: number;
  WaterSetZeroAtMin: boolean;
  PreProductId: IGuid;
  PreMinAmount: number;
  PreAvgAmount: number;
  PreMaxAmount: number;
  Instrument: IGuid;
  Driver: IGuid;
  Machine: IGuid;
}

export interface IPpPlanningWizardDetailsResponsiveData {
  AddProductButtonDisabled$: BehaviorSubject<boolean>;
  PreselectedProductDetail$: BehaviorSubject<IPlantProtectionProductDetails | undefined>;
  PreselectedMinProductRate$: BehaviorSubject<number>;
  PreselectedMaxProductRate$: BehaviorSubject<number>;
  PreselectValidProducts$: BehaviorSubject<IPlantProtectionProduct[]>;
  ProductListItems$: BehaviorSubject<IPpProductListGridItem[]>;
  MaxWaterRange$: BehaviorSubject<number>;
  MinWaterAvg$: BehaviorSubject<number>;
}

export interface IPpProductListGridItem {
  ProductListItem: IPlantProtectionProductList;
  ProductName: string;
  Min: number | undefined;
  Max: number | undefined;
  ApplRate: number;
  UnitExpr: string;
  ButtonIconName: string;
  GrowthStageDefined: boolean;
}

export interface IPpOverviewStatValues {
  NUptakeMin: number;
  NUptakeMax: number;
  NUptakeStepSize: number;
  NUptakeAreaWeightedAvg: number;
  Slope: number;
  WaterAvg: number;
  WaterMin: number;
  WaterMax: number;
}

export interface IPpOverviewStatValues {
  NUptakeMin: number;
  NUptakeMax: number;
  NUptakeStepSize: number;
  NUptakeAreaWeightedAvg: number;
  Slope: number;
  WaterAvg: number;
  WaterMin: number;
  WaterMax: number;
  ProductSum: IPlantProtectionProductList[];
  ProductAreaWeightedAvg: IPlantProtectionProductList[];
}

export interface IProductOption {
  Id: IGuid;
  Name: string;
  EffectiveRange: string;
}
